import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { useRef, useEffect, useCallback, forwardRef } from 'react'
import ReactDOM from 'react-dom'

/**
 * Tab, Tab container
 */
const TabPropTypes = {
  children: PropTypes.node,
  active: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
}

const TabDefaultProps = {
  children: null,
  active: null,
  onChange() {},
  className: '',
}

/**
 * Tab Item
 */
const TabItemPropTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  active: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
}
const TabItemDefaultProps = {
  id: '',
  children: '',
  active: false,
  disabled: false,
  className: '',
  onClick() {},
}

/**
 * TabItem Component
 */
const TabItem = forwardRef((props, ref) => {
  const { children, active, disabled, id, className, onClick } = props
  const tabItemCssClass = ['az-tab__item']
  if (active) tabItemCssClass.push('az-tab__item--active')
  if (disabled) tabItemCssClass.push('az-tab__item--disabled')
  if (className) tabItemCssClass.push(className)

  const _onClick = () => {
    console.log('onClick', id)
    onClick(id)
  }
  return (
    <li ref={ref} data-id={id} className={tabItemCssClass.join(' ')} onClick={_onClick}>
      {children}
    </li>
  )
})

TabItem.displayName = 'TabItem'
TabItem.propTypes = TabItemPropTypes
TabItem.defaultProps = TabItemDefaultProps

/**
 * Tab Component
 */
const Tab = (props) => {
  const { children, className, active, onChange } = props

  const refNav = useRef(null)
  const refTabList = useRef(null)
  const refTabActiveBar = useRef(null)
  const refTabItems = useRef({})

  const setActive = useCallback(
    (id) => {
      onChange(id)
      if (document.body.classList.contains('modal-open')) {
        document.body.classList.remove('modal-open')
      }
    },
    [onChange]
  )

  const scrollElementToView = useCallback((id) => {
    const refNode = refTabItems.current[id]
    if (refNode) {
      const nodeEl = ReactDOM.findDOMNode(refNode)
      const leftPos = Math.max(_.get(nodeEl, 'offsetLeft', 0), 0)
      if (refNav.current) {
        refNav.current.scrollTo(leftPos, 0)
      }
    }
  }, [])

  const scrollTabActiveBar = useCallback((id) => {
    const activeBar = refTabActiveBar.current
    const refNode = refTabItems.current[id]
    if (activeBar && refNode) {
      const activeBarEl = ReactDOM.findDOMNode(activeBar)
      const nodeEl = ReactDOM.findDOMNode(refNode)
      const l = Math.max(_.get(nodeEl, 'offsetParent.offsetLeft', 0) + _.get(nodeEl, 'offsetLeft'), 0)
      const r = _.get(activeBarEl, 'offsetParent.clientWidth', 0) - (l + _.get(nodeEl, 'clientWidth', 0))

      activeBar.style.left = `${l}px`
      activeBar.style.right = `${r}px`
    }
  }, [])

  const calculateListWidth = useCallback(() => {
    const tabList = refTabList.current
    if (tabList) {
      tabList.style.width = 'auto'
      let listWidth = 0
      const listItems = tabList.children || []
      for (const item of listItems) {
        listWidth += item.clientWidth
      }

      tabList.style.width = `${listWidth}px`
    }
  }, [])

  // Debounced functions using useRef to maintain stable references
  const debounceCalculateListWidth = useRef(
    _.debounce(() => {
      calculateListWidth()
    }, 100)
  ).current

  const debounceScrollElementToView = useRef(
    _.debounce((activeId) => {
      scrollElementToView(activeId)
    }, 1000)
  ).current

  // Effect for initial mount and resize listeners
  useEffect(() => {
    const handleResize = () => {
      debounceCalculateListWidth()
      scrollTabActiveBar(active)
      debounceScrollElementToView(active)
    }

    // Initial calculations
    debounceCalculateListWidth()
    debounceScrollElementToView(active)
    scrollTabActiveBar(active)

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      debounceCalculateListWidth.cancel()
      debounceScrollElementToView.cancel()
    }
  }, [])

  // Effect for active tab changes
  useEffect(() => {
    scrollElementToView(active)
    scrollTabActiveBar(active)
  })

  // Effect for children count changes
  const childrenCount = React.Children.count(children)
  const prevChildrenCountRef = useRef(childrenCount)

  useEffect(() => {
    if (prevChildrenCountRef.current !== childrenCount) {
      debounceCalculateListWidth()
      debounceScrollElementToView(active)
      prevChildrenCountRef.current = childrenCount
    }
  }, [childrenCount, active, debounceCalculateListWidth, debounceScrollElementToView])

  return (
    <div className={['az-tab', className].join(' ')}>
      <div className="az-tab__container">
        <nav ref={refNav}>
          <span className="az-tab__active-bar" ref={refTabActiveBar} />
          <ul className="az-tab__list" ref={refTabList}>
            {React.Children.map(children, (child) => {
              if (child && child.type && child.type.displayName === 'TabItem') {
                return React.cloneElement(child, {
                  ...child.props,
                  active: active === child.props.id,
                  onClick: !child.props.disabled && active !== child.props.id ? setActive : () => {},
                  ref: (el) => {
                    refTabItems.current[child.props.id] = el
                  },
                })
              }
              return child
            })}
          </ul>
        </nav>
      </div>
    </div>
  )
}

Tab.displayName = 'Tab'
Tab.propTypes = TabPropTypes
Tab.defaultProps = TabDefaultProps

Tab.Item = TabItem

export default Tab
